/**
 * Phase 16F critical notifications API (plan §14.2: 4 routes).
 * Response shapes are normalised by dtos/notifications/notification.mapper.ts.
 */
import { apiClient } from '../api-client';
import type {
  NotificationAction,
  NotificationActionRequestDto,
  NotificationActionResponseDto,
  NotificationCountResponseDto,
  NotificationDto,
  NotificationListResponseDto,
  NotificationSettingsDto,
  NotificationTab,
  NotificationTaskInput,
  UpdateNotificationSettingsRequestDto,
  WsTicketResponseDto,
} from '../../dtos/notifications/notification.dto';
import {
  mapCount,
  mapNotificationDetail,
  mapNotificationList,
  mapSettings,
  NotificationItem,
  NotificationSettingsModel,
} from '../../dtos/notifications/notification.mapper';

export type NotificationBulkAction = Extract<NotificationAction, 'acknowledge' | 'unacknowledge' | 'clear' | 'restore'>;

export interface NotificationActionResult {
  updated: string[];
  skipped: string[];
  newCount?: number;
}

export class NotificationService {
  /** My New (unacknowledged) count; the badge. */
  static async getCount(): Promise<number> {
    const response = await apiClient.get<NotificationCountResponseDto>('/notifications', { view: 'count' });
    if (response.success) return mapCount(response.data);
    throw new Error(response.error?.message || 'Could not load the notification count');
  }

  /** One inbox tab or filter, newest first. */
  static async list(tab: NotificationTab): Promise<NotificationItem[]> {
    const response = await apiClient.get<NotificationListResponseDto | NotificationDto[]>('/notifications', { view: 'list', tab });
    if (response.success) return mapNotificationList(response.data);
    throw new Error(response.error?.message || 'Could not load notifications');
  }

  /** One notification (deep link after login). */
  static async get(notificationId: string): Promise<NotificationItem> {
    const response = await apiClient.get<NotificationDto | { notification: NotificationDto }>('/notifications', { id: notificationId });
    const item = response.success ? mapNotificationDetail(response.data) : null;
    if (item) return item;
    throw new Error(response.error?.message || 'This notification is no longer available');
  }

  /** Acknowledge / unacknowledge (≤24 h) / clear / restore my own recipient state. */
  static async act(action: NotificationBulkAction, ids: string[]): Promise<NotificationActionResult> {
    const body: NotificationActionRequestDto = { action, ids };
    const response = await apiClient.post<NotificationActionResponseDto>('/notifications', body);
    if (response.success) {
      return { updated: response.data?.updated || ids, skipped: response.data?.skipped || [], newCount: response.data?.newCount };
    }
    throw new Error(response.error?.message || 'Could not update notifications');
  }

  /** "Notify the health plan" done on a PATIENT_HOSPITALIZED notification. */
  static async completeTask(notificationId: string, task: NotificationTaskInput): Promise<void> {
    // The API takes doneDate (YYYY-MM-DD the health plan was told), method and contact
    const body: NotificationActionRequestDto = { action: 'task', notificationId, task: { doneDate: task.date, method: task.method, contact: task.contact } };
    const response = await apiClient.post<NotificationActionResponseDto>('/notifications', body);
    if (!response.success) throw new Error(response.error?.message || 'Could not save the health plan notification');
  }

  /** Single-use WebSocket connect ticket (valid 30 s). */
  static async getWsTicket(): Promise<string> {
    const body: NotificationActionRequestDto = { action: 'ws-ticket' };
    const response = await apiClient.post<WsTicketResponseDto>('/notifications', body);
    if (response.success && response.data?.ticket) return response.data.ticket;
    throw new Error(response.error?.message || 'Could not open the notification connection');
  }

  static async getSettings(): Promise<NotificationSettingsModel> {
    const response = await apiClient.get<NotificationSettingsDto>('/admin/notification-settings');
    if (response.success) return mapSettings(response.data);
    throw new Error(response.error?.message || 'Could not load notification settings');
  }

  static async updateSettings(model: NotificationSettingsModel): Promise<NotificationSettingsModel> {
    const body: UpdateNotificationSettingsRequestDto = {
      triggers: Object.fromEntries(Object.entries(model.roles).map(([code, roles]) => [code, { roles }])),
      missedClockInGraceMinutes: model.missedClockInGraceMinutes,
      ...(model.version !== undefined ? { version: model.version } : {}),
    };
    const response = await apiClient.put<NotificationSettingsDto>('/admin/notification-settings', body);
    if (response.success) return response.data ? mapSettings(response.data) : model;
    throw new Error(response.error?.message || 'Could not save notification settings');
  }
}

export default NotificationService;
